import { useMemo } from 'react';

import { filterMenuByRoles, transformMenuItems } from '@/config/navigation/transformer';
import type { CodeString } from '@/consts/codes';
import type { MenuItem } from '@/consts/navigation';
import { buildDynamicSection } from '@/features/navigation/utils/buildDynamicSection';
import { filterHiddenItems } from '@/features/navigation/utils/filterHiddenItems';
import { selectUser, useAuthStore } from '@/stores/useAuthStore';
import { useFavoritesStore } from '@/stores/useFavoritesStore';
import { useRecentsStore } from '@/stores/useRecentsStore';

import { useMenuBadges } from './useMenuBadges';
import { useMenuConfig } from './useMenuConfig';

export interface DynamicMenuResult {
  /** Menu for rendering — hidden items and empty containers removed. */
  menu: MenuItem[];
  /** Full menu including hidden items — for breadcrumb/route resolution. */
  fullMenu: MenuItem[];
  allMenuItems: MenuItem[];
  addRecent: (code: CodeString | undefined) => Promise<void>;
}

export const useDynamicMenu = (): DynamicMenuResult => {
  const user = useAuthStore(selectUser);
  const { menuItems } = useMenuConfig();

  const userRoles = useMemo(() => {
    if (!user?.Roles) return [];
    return user.Roles.map((role) => role.Id);
  }, [user?.Roles]);

  // Filter by roles, transform to rendered MenuItem[] (hidden items preserved for resolution)
  const fullMenu = useMemo(() => {
    const filtered = filterMenuByRoles(menuItems, userRoles);
    return transformMenuItems(filtered);
  }, [menuItems, userRoles]);

  // Flatten visible items with codes for recents/favorites resolution.
  // filterHiddenItems drops hidden items and the whole subtree of a hidden
  // group, so codes under a hidden group no longer resolve into Favorites/Recent.
  const allMenuItems = useMemo(() => {
    const flatten = (items: MenuItem[]): MenuItem[] =>
      items.flatMap((item) => {
        const nested = item.children ? flatten(item.children) : [];
        return item.code ? [item, ...nested] : nested;
      });
    return filterHiddenItems(fullMenu).flatMap((section) => flatten(section.children ?? []));
  }, [fullMenu]);

  const { favoriteCodes, favoritesLoading } = useFavoritesStore();
  const { recentCodes, recentsLoading, addRecent } = useRecentsStore();

  const menuWithDynamicSections = useMemo(() => {
    return fullMenu.map((section) => {
      if (section.text === 'navigation.favorites.title') {
        return buildDynamicSection(
          allMenuItems,
          section,
          favoriteCodes,
          favoritesLoading,
          'navigation.favorites.children.empty'
        );
      }
      if (section.text === 'navigation.recent.title') {
        return buildDynamicSection(
          allMenuItems,
          section,
          recentCodes,
          recentsLoading,
          'navigation.recent.children.empty'
        );
      }
      return section;
    });
  }, [fullMenu, allMenuItems, favoriteCodes, favoritesLoading, recentCodes, recentsLoading]);

  const menuWithBadges = useMenuBadges(menuWithDynamicSections);

  // Drop hidden items + empty containers — only for rendering, not for resolution
  const renderedMenu = useMemo(() => filterHiddenItems(menuWithBadges), [menuWithBadges]);

  return { menu: renderedMenu, fullMenu: menuWithBadges, allMenuItems, addRecent };
};
